import { useState, useEffect, useCallback, useMemo } from 'react';
import type { AbsenceType } from '../types';
import { useAPIStorage } from './useAPIStorage';
import { storageLogger } from '../utils/logger';

/**
 * Hook zum Laden und Cachen der aktiven Abwesenheitsarten.
 * Lädt einmal bei Initialisierung und bietet eine reload-Funktion.
 */
export function useAbsenceTypes() {
  const { getAbsenceTypes } = useAPIStorage();
  const [absenceTypes, setAbsenceTypes] = useState<AbsenceType[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const types = await getAbsenceTypes();
      setAbsenceTypes(types);
    } catch (err) {
      storageLogger.error('Fehler beim Laden der Abwesenheitsarten', err instanceof Error ? err : undefined);
    } finally {
      setLoading(false);
    }
  }, [getAbsenceTypes]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Typ nach systemKey finden */
  const getBySystemKey = useCallback(
    (key: string): AbsenceType | undefined =>
      absenceTypes.find((t) => t.systemKey === key),
    [absenceTypes]
  );

  /** Typ nach ID finden */
  const getById = useCallback(
    (id: string): AbsenceType | undefined =>
      absenceTypes.find((t) => t.id === id),
    [absenceTypes]
  );

  /** Nur urlaubswirksame Typen (für VacationRangeDialog) */
  const vacationTypes = useMemo(
    () => absenceTypes.filter((t) => t.countsAsVacation),
    [absenceTypes]
  );

  return {
    absenceTypes,
    vacationTypes,
    loading,
    reload: load,
    getBySystemKey,
    getById,
  };
}
